import * as React from 'react';
import { Component } from 'react';

import connectToRouter from 'js/lib/connectToRouter';
import type { InjectedRouter } from 'js/lib/connectToRouter';

import { StyleSheet, css } from '@coursera/coursera-ui';

import * as actions from 'bundles/authentication/shared/actions';
import AuthenticationContentTracker from 'bundles/authentication/shared/components/authentication-modal/AuthenticationContentTracker';
import CompleteSSO from 'bundles/authentication/shared/components/authentication-modal/CompleteSSO';
import ExpiredLink from 'bundles/authentication/shared/components/authentication-modal/ExpiredLink';
import ForgotPassword from 'bundles/authentication/shared/components/authentication-modal/ForgotPassword';
import LinkedAccount from 'bundles/authentication/shared/components/authentication-modal/LinkedAccount';
import Login from 'bundles/authentication/shared/components/authentication-modal/Login';
import MagicLink from 'bundles/authentication/shared/components/authentication-modal/MagicLink';
import OrganizationSSO from 'bundles/authentication/shared/components/authentication-modal/OrganizationSSO';
import RequestMagicLink from 'bundles/authentication/shared/components/authentication-modal/RequestMagicLink';
import SSO from 'bundles/authentication/shared/components/authentication-modal/SSO';
import SetPassword from 'bundles/authentication/shared/components/authentication-modal/SetPassword';
import Signup from 'bundles/authentication/shared/components/authentication-modal/Signup';
import type SSOError from 'bundles/authentication/shared/components/authentication-modal/SocialSSO/SSOError';
import { AuthModalTrackerContextProvider } from 'bundles/authentication/shared/components/authentication-modal/contexts/AuthenticationTracking';
import { isTermsOfUseRolloutForUS } from 'bundles/authentication/utils';

export enum MODES {
  COMPLETE = 'complete',
  ERROR = 'error',
  EXPIREDLINK = 'expired',
  FORGOT = 'forgot',
  LINKED = 'linked',
  LOGIN = 'login',
  MAGICLINK = 'magiclink',
  ORGANIZATION = 'organization',
  REQUESTMAGICLINK = 'requestmagiclink',
  SIGNUP = 'signup',
  SSO = 'sso',
  SETPASSWORD = 'setpassword',
}

type Context = Record<string, any>;

type PropsFromRouter = {
  router: InjectedRouter;
};

export type Props = {
  defaultMode?: MODES.SSO | MODES.SETPASSWORD | MODES.LOGIN | MODES.SIGNUP | MODES.EXPIREDLINK;
  context: Context;
  redirectTo?: string;
  preventRegister?: boolean;
  magicLinkRedirect?: string;
  isStandaloneAuth?: boolean;
};

type PropsToComponent = PropsFromRouter & Props;

type State = {
  context: Context;
  mode: MODES;
};

const VIEWS = {
  complete: CompleteSSO,
  error: Login,
  expired: ExpiredLink,
  forgot: ForgotPassword,
  linked: LinkedAccount,
  login: Login,
  magiclink: MagicLink,
  organization: OrganizationSSO,
  requestmagiclink: RequestMagicLink,
  signup: Signup,
  sso: SSO,
  setpassword: SetPassword,
};

const styles = StyleSheet.create({
  container: {
    maxHeight: '100%',
    height: '100vh',
    overflow: 'auto',
    position: 'relative',
    '@media (min-width: 600px)': {
      height: 'auto',
    },
  },
  header: {
    '@media (min-width: 600px)': {
      display: 'none !important',
    },
  },
});

export class AuthenticationContent extends Component<PropsToComponent, State> {
  state = {
    context: this.props.context ?? {},
    mode: this.props.defaultMode || MODES.LOGIN,
  };

  // add epic variable for XDPs CRO

  navigate = (mode: MODES, options: Context = {}) => {
    const { context: routeContext, router, isStandaloneAuth } = this.props;
    const areModesTheSame = router.location.pathname.includes(`/${mode}`);
    if (isStandaloneAuth && !areModesTheSame && (mode === MODES.SIGNUP || mode === MODES.LOGIN)) {
      router.replace(`/${mode}${router.location.search}`);
    } else {
      this.setState({ context: { ...routeContext, ...options }, mode });
    }
  };

  onLogin = (redirectURL?: string) => {
    const { redirectTo } = this.props;

    actions.onLogin(redirectTo || redirectURL);
  };

  onSignup = (redirectURL?: string, options?: { addShowTouAcceptParam?: boolean }) => {
    const { redirectTo } = this.props;

    actions.onSignup(redirectTo || redirectURL, options);
  };

  onSSOError = (error: SSOError) => {
    const { mode } = this.state;
    const { code } = error;

    switch (code) {
      case 'accountLinkedToSocialAccount':
        return this.navigate(MODES.LINKED, { accountType: error.response?.existingAccountType });

      case 'existingCourseraAccount':
      case 'noEmailAvailable':
      default:
        return this.navigate(MODES.COMPLETE, {
          mode: code,
          referrer: mode,
          service: error.service,
        });
    }
  };

  onSSOSuccess = (isRegistration: boolean) => {
    if (isRegistration) {
      this.onSignup(undefined, { addShowTouAcceptParam: isTermsOfUseRolloutForUS() });
    } else {
      this.onLogin();
    }
  };

  render() {
    const { preventRegister, redirectTo, magicLinkRedirect } = this.props;
    const { context, mode } = this.state;
    const Content = VIEWS[mode] || Login;
    let authModal;

    if (mode === MODES.LOGIN || mode === MODES.SIGNUP) {
      authModal = { modalName: mode };
    }

    return (
      <section {...css(styles.container)}>
        <AuthModalTrackerContextProvider value={authModal}>
          <AuthenticationContentTracker />
          {/* @ts-expect-error ts-migrate(2604) FIXME: JSX element type 'Content' does not have any const... Remove this comment to see the full error message */}
          <Content
            redirect={redirectTo}
            magicLinkRedirect={magicLinkRedirect}
            navigate={this.navigate}
            onLogin={this.onLogin}
            onSignup={this.onSignup}
            onSSOError={this.onSSOError}
            onSSOSuccess={this.onSSOSuccess}
            preventRegister={preventRegister}
            {...context}
          />
        </AuthModalTrackerContextProvider>
      </section>
    );
  }
}

export default connectToRouter<PropsToComponent, Props>((router) => ({ router }))(AuthenticationContent);
